import React from 'react';
import { cn } from '../../lib/utils';

// X Icon component for close button
const XIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const Dialog = ({ children, open, onOpenChange }) => {
  // Simple container component - the actual modal rendering is handled by DialogContent
  return (
    <div 
      data-dialog-root="true"
      data-open={open}
    >
      {children}
    </div>
  );
};

const DialogTrigger = ({ asChild, children, onClick, ...props }) => {
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      onClick: onClick,
      ...props
    });
  }

  return (
    <button
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

const DialogContent = ({ 
  className, 
  children, 
  showClose = true,
  open,
  onClose,
  ...props 
}) => {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      onClick={() => onClose && onClose()}
    >
      <div
        className={cn(
          "relative bg-white rounded-lg border shadow-lg w-full max-w-lg p-6",
          className
        )}
        onClick={(e) => e.stopPropagation()}
        {...props}
      >
        {showClose && (
          <button
            className="absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none"
            onClick={() => onClose && onClose()}
          >
            <XIcon />
          </button>
        )}
        {children}
      </div>
    </div>
  );
};

const DialogHeader = ({ className, children, ...props }) => {
  return (
    <div
      className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
      {...props}
    >
      {children}
    </div>
  );
};

const DialogTitle = ({ className, children, ...props }) => {
  return (
    <h3
      className={cn("text-lg font-semibold leading-none tracking-tight", className)}
      {...props}
    >
      {children}
    </h3>
  );
};

const DialogDescription = ({ className, children, ...props }) => {
  return (
    <p
      className={cn("text-sm text-gray-600", className)}
      {...props}
    >
      {children}
    </p>
  );
};

const DialogFooter = ({ className, children, ...props }) => {
  return (
    <div
      className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
      {...props}
    >
      {children}
    </div>
  );
};

export { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter };